import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Search, 
  X, 
  Users, 
  ChevronDown, 
  UserPlus,
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight 
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/api';
import { ClientesTable, ClienteDetailsDrawer, CreateClienteDrawer, EditClienteDrawer, type Cliente } from '../components/clientes';
import { ClientesService } from '../services/clientes/clientes.service';
import { ConfirmationToast } from '../components/ui/ConfirmationToast';
import { usePermissions } from '../hooks/usePermissions';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

interface PaginatedClientesResponse {
  data: Cliente[];
  meta: PaginationMeta;
}

interface StatsData {
  totalClientes: number;
  nuevosEsteMes: number;
  activos: number;
  inactivos: number;
}

const fetchClientes = async (page: number = 1, limit: number = 50, searchTerm?: string): Promise<PaginatedClientesResponse> => {
  return await ClientesService.getClientes(page, limit, searchTerm);
};

const fetchStats = async (): Promise<StatsData> => {
  return await ClientesService.getStats();
};

export const ClientesPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Drawers
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [clienteToEdit, setClienteToEdit] = useState<Cliente | null>(null);

  // Confirmaciones
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmToggle, setShowConfirmToggle] = useState(false);
  const [clienteToAction, setClienteToAction] = useState<Cliente | null>(null);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms de delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda debounced
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Cerrar dropdown cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const { 
    data, 
    isLoading, 
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedClientesResponse>({
    queryKey: ['clientes', page, limit, debouncedSearchTerm],
    queryFn: () => fetchClientes(page, limit, debouncedSearchTerm),
    placeholderData: (previousData) => previousData,
  });

  const clientes = data?.data || [];
  const paginationMeta = data?.meta;

  // Query para estadísticas
  const { 
    data: statsData, 
    isLoading: isLoadingStats 
  } = useQuery<StatsData>({
    queryKey: ['clientes-stats'],
    queryFn: fetchStats,
    enabled: activeTab === 'stats', // Solo cargar cuando esté en la pestaña de estadísticas
  });

  // Validación adicional para asegurar que clientes sea siempre un array
  const safeClientes = Array.isArray(clientes) ? clientes : [];

  // Mantener foco en el input después de la búsqueda
  useEffect(() => {
    if (!isFetching && searchTerm && searchInputRef.current) {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetching, searchTerm]);

  const handleClienteClick = (cliente: Cliente) => {
    setSelectedCliente(cliente);
  };

  const handleCloseDrawer = () => {
    setSelectedCliente(null);
  };

  const handleOpenCreateCliente = () => {
    setIsCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setIsCreateOpen(false);
  };

  const handleCreateSuccess = () => {
    refetch();
  };

  const handleEdit = (cliente: Cliente) => {
    setClienteToEdit(cliente);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setClienteToEdit(null);
  };

  const handleEditSuccess = () => {
    refetch();
  };

  const handleDelete = (cliente: Cliente) => {
    setClienteToAction(cliente);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!clienteToAction) return;
    try {
      await ClientesService.deleteCliente(clienteToAction.id);
      refetch();
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
    } finally {
      setShowConfirmDelete(false);
      setClienteToAction(null);
    }
  };

  const handleToggleStatus = (cliente: Cliente) => {
    setClienteToAction(cliente);
    setShowConfirmToggle(true);
  };

  const confirmToggle = async () => {
    if (!clienteToAction) return;
    try {
      await ClientesService.toggleStatus(clienteToAction.id);
      refetch();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    } finally {
      setShowConfirmToggle(false);
      setClienteToAction(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Resetear a la primera página cuando cambie el límite
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error al cargar los clientes</div>
          <button 
            onClick={() => refetch()}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Gestión de Clientes</h1>
            <p className="text-gray-600 mt-1">
              Administra los clientes registrados en el sistema
            </p>
          </div>
        </div>
        
        {/* Pestañas minimalistas */}
        <div className="flex space-x-8 mb-6">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'text-emerald-700 border-emerald-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Lista de Clientes
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'stats'
                ? 'text-emerald-700 border-emerald-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Dashboard Estadístico
          </button>
        </div>
        
        {/* Barra de búsqueda - Solo mostrar en la pestaña de lista */}
        {activeTab === 'list' && (
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isFetching && searchTerm ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar por nombre, email, teléfono o documento..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm placeholder-gray-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center space-x-3 flex-wrap gap-y-2">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <div className="text-sm">
                    {searchTerm ? (
                      <div className="flex items-center space-x-1">
                        <span className="font-semibold text-emerald-700">{paginationMeta ? paginationMeta.total : safeClientes.length}</span>
                        <span className="text-gray-600">resultados encontrados</span>
                        <Search className="w-4 h-4 text-emerald-500 ml-1" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <span className="font-semibold text-emerald-700">{paginationMeta ? paginationMeta.total : safeClientes.length}</span>
                        <span className="text-gray-600">clientes registrados</span>
                        <Users className="w-4 h-4 text-emerald-500 ml-1" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Selector de cantidad por página - Elegante */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 font-medium">Mostrar</span>
                <div className="relative" data-dropdown>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all cursor-pointer flex items-center min-w-15"
                  >
                    {limit}
                  </button>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                      {[10, 20, 30, 50, 100].map((value) => (
                        <div
                          key={value}
                          onClick={() => {
                            handleLimitChange(value);
                            setIsDropdownOpen(false);
                          }}
                          className="px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botón Crear Cliente */}
              {hasPermission('Clientes', 'crear') && (
                <button
                  onClick={handleOpenCreateCliente}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>Crear Cliente</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contenido de las pestañas */}
      {activeTab === 'list' ? (
        <>
          {/* Tabla de clientes */}
          <div className="relative">
            <ClientesTable
              clientes={safeClientes}
              onClienteClick={handleClienteClick}
              isLoading={isLoading && safeClientes.length === 0}
            />
          </div>

          {/* Paginación Elegante */}
          {paginationMeta && (
            <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
              <div className="flex items-center justify-between">
                {/* Información de resultados */}
                <div className="flex items-center space-x-6">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">
                  {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-semibold text-gray-900">{paginationMeta.total}</span>
                <span className="ml-1">resultados</span>
              </div>
              
              {/* Páginas X de Y */}
              <div className="text-sm text-gray-500">
                Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
              </div>
            </div>
            
            {/* Controles de paginación */}
            <div className="flex items-center space-x-1">
              {/* Botón primera página */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              {/* Botón página anterior */}
              <button
                onClick={handlePreviousPage}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Páginas centrales */}
              <div className="flex items-center space-x-1 mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 3;
                  let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  // Mostrar primera página si no está en el rango
                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  // Páginas del rango central
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                          i === paginationMeta.page
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Mostrar última página si no está en el rango
                  if (end < paginationMeta.totalPages) {
                    if (end < paginationMeta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={paginationMeta.totalPages}
                        onClick={() => handlePageChange(paginationMeta.totalPages)}
                        className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {paginationMeta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Botón página siguiente */}
              <button
                onClick={handleNextPage}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Botón última página */}
              <button
                onClick={() => handlePageChange(paginationMeta.totalPages)}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Dashboard Estadístico */
        <div className="p-6">
          {isLoadingStats ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : statsData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-sm font-medium text-gray-500">Total Clientes</div>
                <div className="mt-2 text-3xl font-bold text-gray-900">{statsData.totalClientes}</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-sm font-medium text-gray-500">Nuevos este mes</div>
                <div className="mt-2 text-3xl font-bold text-emerald-600">{statsData.nuevosEsteMes}</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-sm font-medium text-gray-500">Activos</div>
                <div className="mt-2 text-3xl font-bold text-green-600">{statsData.activos}</div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-sm font-medium text-gray-500">Inactivos</div>
                <div className="mt-2 text-3xl font-bold text-red-600">{statsData.inactivos}</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">No hay estadísticas disponibles</div>
          )}
        </div>
      )}

      {/* Drawer de detalles del cliente */}
      <ClienteDetailsDrawer
        isOpen={selectedCliente !== null}
        cliente={selectedCliente}
        onClose={handleCloseDrawer}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        canEdit={hasPermission('Clientes', 'editar')}
        canDelete={hasPermission('Clientes', 'eliminar')}
      />

      {/* Drawer de crear cliente */}
      <CreateClienteDrawer
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
        onSuccess={handleCreateSuccess}
      />

      {/* Drawer de editar cliente */}
      <EditClienteDrawer
        isOpen={isEditOpen}
        cliente={clienteToEdit}
        onClose={handleCloseEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Confirmación de eliminación */}
      <ConfirmationToast
        isOpen={showConfirmDelete}
        title="Eliminar Cliente"
        message={`¿Estás seguro de que deseas eliminar a ${clienteToAction?.nombres} ${clienteToAction?.apellidos}? Esta acción no se puede deshacer.`}
        type="danger"
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => { setShowConfirmDelete(false); setClienteToAction(null); }}
      />

      {/* Confirmación de cambio de estado */}
      <ConfirmationToast
        isOpen={showConfirmToggle}
        title="Cambiar Estado"
        message={`¿Deseas ${clienteToAction?.estado === 'activo' ? 'desactivar' : 'activar'} a ${clienteToAction?.nombres} ${clienteToAction?.apellidos}?`}
        type="warning"
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={confirmToggle}
        onCancel={() => { setShowConfirmToggle(false); setClienteToAction(null); }}
      />
    </div>
  );
};

export default ClientesPage;
